import React, { useMemo, useCallback } from "react";
import { Table, Button, Alert, message, Modal, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useSupplies } from "../context/SuppliesContext";

const PurchasePlan = () => {
    const { planData, loading, handleGeneratePdf, periodStatuses, periods } = useSupplies();

    const handleGeneratePdfForPeriod = useCallback(
        (period) => {
            const itemsForPeriod = planData
                .filter(item => (item[period.key] || 0) > 0)
                .map(item => ({
                    name: item.name,
                    unit: item.unit,
                    quantity: item[period.key],
                }));

            if (itemsForPeriod.length === 0) {
                message.warning("Нет позиций для закупки в этом периоде");
                return;
            }

            const currentStatus = periodStatuses?.find(s => s.periodKey === period.key);
            if (currentStatus) {
                Modal.confirm({
                    title: "Заявка уже сформирована",
                    content: "Документ для этого периода уже был сгенерирован. Вы уверены, что хотите сформировать его повторно?",
                    okText: "Да, сформировать",
                    cancelText: "Отмена",
                    onOk: () => handleGeneratePdf(itemsForPeriod, period),
                });
                return;
            }

            handleGeneratePdf(itemsForPeriod, period);
        },
        [planData, handleGeneratePdf, periodStatuses]
    );

    // ─── Базовые колонки ────────────────────────────────────────────────────

    const baseColumns = useMemo(() => [
        {
            title: "№",
            dataIndex: "num",
            key: "num",
            width: 40,
            align: "center",
            fixed: "left",
        },
        {
            title: "Наименование",
            dataIndex: "name",
            key: "name",
            width: 170,
            fixed: "left",
            render: (text, record) => (
                <span title={record.isFallback ? "Позиция посчитана через fallback (по имени)" : ""}>
                    {text}
                    {record.isFallback && (
                        <span style={{ color: "#faad14", fontWeight: "bold" }}> *</span>
                    )}
                </span>
            ),
        },
        {
            title: "Ед. изм",
            dataIndex: "unit",
            key: "unit",
            width: 80,
            align: "center",
        },
        {
            title: "Срок дост.",
            dataIndex: "deliveryDays",
            key: "deliveryDays",
            width: 85,
            align: "center",
            sorter: (a, b) => (a.deliveryDays || 2) - (b.deliveryDays || 2),
            render: (val) => {
                const days = val || 2;
                const isLong = days >= 30;
                return (
                    <span style={{ color: isLong ? "#ff4d4f" : "#8c8c8c", fontWeight: isLong ? 700 : 400 }}>
                        {days} дн.
                    </span>
                );
            },
        },
        {
            title: "Мин запас",
            dataIndex: "min",
            key: "min",
            width: 85,
            align: "center",
        },
        {
            title: "Макс запас",
            dataIndex: "max",
            key: "max",
            width: 85,
            align: "center",
        },
        {
            title: (
                <Tooltip title="Общая потребность из ВСЕХ активных заказов (включая будущие месяцы). Может быть больше суммы недель, если есть заказы с дедлайном за пределами текущего месяца.">
                    <span>
                        Необходимо <InfoCircleOutlined style={{ fontSize: 12, color: "#1890ff" }} />
                    </span>
                </Tooltip>
            ),
            dataIndex: "needed",
            key: "needed",
            width: 110,
            align: "center",
            onCell: () => ({ style: { backgroundColor: "#fffbe6" } }),
            render: (_, record) => {
                const total = record.needed || 0;
                const thisMonth = record.neededThisMonth || 0;
                const hasOverflow = total > thisMonth && thisMonth > 0;

                return (
                    <Tooltip
                        title={
                            hasOverflow
                                ? `Из них ${total - thisMonth} шт. — заказы за пределами текущего месяца`
                                : undefined
                        }
                    >
                        <span style={{ fontWeight: 600, color: "#d48806" }}>
                            {total}
                            {hasOverflow && (
                                <sup style={{ color: "#ff4d4f", fontSize: 10, marginLeft: 2 }}>
                                    +{total - thisMonth}
                                </sup>
                            )}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: "На складе",
            dataIndex: "inStock",
            key: "inStock",
            width: 90,
            align: "center",
            onCell: () => ({ style: { backgroundColor: "#f6ffed" } }),
            render: (val) => (
                <span style={{ fontWeight: 600, color: "#389e0d" }}>
                    {val || 0}
                </span>
            ),
        },
        {
            title: "Заказать",
            dataIndex: "toOrder",
            key: "toOrder",
            width: 85,
            align: "center",
            onCell: () => ({ style: { backgroundColor: "#fff1f0" } }),
            render: (val) => {
                if (val === "" || val === undefined || val === null) return "";
                if (val <= 0) return <span style={{ color: "#8c8c8c" }}>0</span>;
                return <span style={{ fontWeight: 700, color: "#cf1322" }}>{val}</span>;
            },
        },
    ], []);

    // ─── Колонки периодов ───────────────────────────────────────────────────

    const periodColumns = useMemo(() => {
        return periods.map(period => {
            const pStatus = periodStatuses?.find(s => s.periodKey === period.key);
            const isFormed = pStatus?.status === "FORMED";
            const isDelivered = pStatus?.status === "DELIVERED";

            let btnText = "Сформировать закупку";
            let btnType = "default";
            let btnDisabled = false;
            let bgColor = "inherit";

            if (isDelivered) {
                btnText = "Доставлено ✅";
                btnType = "primary";
                btnDisabled = true;
                bgColor = "#f6ffed";
            } else if (isFormed) {
                btnText = "Сформировано ✅";
                bgColor = "#fffbe6";
            }

            return {
                title: (
                    <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 12, marginBottom: 6 }}>{period.label}</div>
                        <div style={{ display: "flex", gap: 4, alignItems: "center", justifyContent: "center" }}>
                            <Button
                                type={btnType}
                                size="small"
                                disabled={btnDisabled}
                                onClick={() => handleGeneratePdfForPeriod(period)}
                                style={{ fontSize: 11, width: "100%" }}
                            >
                                {btnText}
                            </Button>
                        </div>
                    </div>
                ),
                dataIndex: period.key,
                key: period.key,
                width: 140,
                align: "center",
                onCell: () => ({ style: { backgroundColor: bgColor } }),
                render: (val) => {
                    if (!val) return <span style={{ color: "#d9d9d9" }}>—</span>;
                    return <span style={{ fontWeight: 600 }}>{Math.round(val)}</span>;
                },
            };
        });
    }, [periods, periodStatuses, handleGeneratePdfForPeriod]);

    const columns = useMemo(
        () => [...baseColumns, ...periodColumns],
        [baseColumns, periodColumns]
    );

    return (
        <div style={{ marginTop: 16 }}>
            <Alert
                message="План закупок"
                description={
                    <div>
                        <div><b>Минимальный запас на складе</b> = Медиана (средний расход).</div>
                        <div><b>Максимальный запас на складе</b> = Медиана + 50%.</div>
                        <div><b>На складе</b> = текущий остаток из БД.</div>
                        <div>
                            <b>Необходимо</b> = Общая потребность из всех активных заказов.{" "}
                            <span style={{ color: "#8c8c8c" }}>
                                Может превышать сумму недель, если есть заказы с дедлайном в будущих месяцах.
                            </span>
                        </div>
                    </div>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />
            <div style={{ overflowX: "auto" }}>
                <Table
                    size="small"
                    bordered
                    columns={columns}
                    dataSource={planData}
                    pagination={false}
                    loading={loading}
                    scroll={{ x: 1500 }}
                    style={{ minWidth: 1100 }}
                    rowClassName={record => {
                        if (record.min > 0 && record.inStock < record.min) {
                            return "row-warning";
                        }
                        return "";
                    }}
                />
            </div>
        </div>
    );
};

export default PurchasePlan;